<script lang="ts">
export const elInputChatColors = ['primary', 'secondary'] as const;
export type ElInputChatColors = (typeof elInputChatColors)[number];
</script>

<script setup lang="ts">
import { computed, nextTick, ref } from 'vue';
import ElIconButton from './ElIconButton.vue';
import ElFile from './ElFile.vue';
import ElInputTextarea from './forms/ElInputTextarea.vue';
import { TextualValueType } from './forms/input';

withDefaults(defineProps<{ color?: ElInputChatColors; error?: string }>(), {
  color: 'primary',
  error: undefined,
});

const emit = defineEmits<{
  onSend: [message?: string, files?: File[]];
}>();

const container = ref<HTMLDivElement>();
const inputField = ref<HTMLFormElement>();
const message = ref<string>();
const files = ref<File[]>([]);

const isEmptyMessage = computed(() => !message.value && !files.value.length);

const textareaMaxHeight = computed(() => {
  if (files.value.length) return 'max-h-[9.5rem]';

  return 'max-h-[12.5rem]';
});

const onChangeFile = async (event: Event) => {
  const target = event.target as HTMLInputElement;
  if (!target || !target.files || !target.files.length) return;

  files.value = [...files.value, ...target.files];

  if (inputField.value) inputField.value.value = null;

  const textarea = container.value?.getElementsByTagName('textarea')?.[0];
  if (!textarea) return;

  // Await next vue render and scroll to end of textarea
  await nextTick();
  textarea.scrollTop = textarea.scrollHeight;
};

// Resize textarea height based on it's content while typing
const onChangeText = (value: TextualValueType) => {
  const textarea = container.value?.getElementsByTagName('textarea')?.[0];
  if (!textarea) return;

  // Set height to default min height which is 2.5rem when text is empty
  textarea.style.setProperty('height', '2.5rem');

  if (!value) return;

  // Calculate text rows based on line height and scroll height
  let rows = undefined;
  if (document && document.defaultView) {
    const lineHeight = Number(document.defaultView.getComputedStyle(textarea, null).getPropertyValue('line-height').replace('px', ''));
    // Calculate rows removing 16px of padding
    rows = Math.floor((textarea.scrollHeight - 16) / lineHeight);
  }

  // Set empty height to shrink as well as stretch
  textarea.style.setProperty('height', '');
  // Set height and add 3 px if there's more than 1 rows to hide scrollbar
  textarea.style.setProperty('height', `${textarea.scrollHeight + ((rows ?? 0) > 1 ? 3 : 0)}px`);
};

const handleSubmit = () => {
  emit('onSend', message.value, files.value);

  // After submit the message, clear textarea and files
  message.value = undefined;
  files.value = [];
};
</script>

<template>
  <div ref="container" class="flex flex-col gap-2 px-4 py-5 w-full h-auto bg-neutral-surface border-t border-neutral-surface">
    <div v-if="files.length" class="flex w-full gap-2 max-sm:overflow-scroll sm:overflow-x-auto max-sm:hide-scrollbar">
      <div v-for="(file, index) in files" :key="`file-${index}`" class="max-w-44">
        <ElFile variant="primary" :name="file.name" @close="files.splice(index, 1)" />
      </div>
    </div>

    <div class="flex gap-4">
      <div class="shrink-0 w-8">
        <input
          ref="inputField"
          type="file"
          class="hidden"
          :accept="'*'"
          :disabled="!!error"
          :multiple="true"
          @change.prevent="onChangeFile"
        />

        <ElIconButton
          v-if="inputField"
          :icon="{ name: 'PaperClipIcon' }"
          :disabled="!!error"
          class="h-full flex items-end pb-1"
          @click="inputField.click()"
        />
      </div>

      <ElInputTextarea
        v-model="message"
        :placeholder="error"
        :disabled="!!error"
        class="w-full"
        :error-message="!!error ? 'Errore' : undefined"
        :hidden-error-message="true"
        :textarea-class="`min-h-10 ${textareaMaxHeight} ${!!error && 'placeholder:!text-error'}`"
        @update:model-value="onChangeText"
      />

      <ElIconButton
        :icon="{ name: 'PaperAirplaneIcon' }"
        class="shrink-0 w-8 flex items-end pb-1"
        :disabled="!!error || isEmptyMessage"
        @click="handleSubmit"
      />
    </div>
  </div>
</template>
